/* eslint-disable no-dupe-class-members */
import { CompleteCallback, Sound, sound as PixiSoundType } from "@pixi/sound";
import { gsap } from "gsap";

/**
 * Options for playing a sound
 */
interface PlaySoundOptions {
    /**
     * Callback when the sound has completed its playback
     */
    completeCallback?: CompleteCallback;
    /**
     * Ignore if the sound is already playing
     */
    ignorePlaying?: boolean;
    /**
     * Stop all sounds before playing
     */
    stopSounds?: boolean;
}

export default class SoundManager {
    //--------------------------------------------------------------------------
    //
    //  Properties
    //
    //--------------------------------------------------------------------------

    //----------------------------------
    //  Private:
    //----------------------------------

    private static _instance: SoundManager = new SoundManager();
    public lastSoundAlias: string = "";
    private soundContext: typeof PixiSoundType | null = null;

    public get isPlaying(): boolean {
        return this.soundContext?.isPlaying() ?? false;
    }

    //--------------------------------------------------------------------------
    //
    //  Constructor
    //
    //--------------------------------------------------------------------------

    private constructor() {
        if (SoundManager._instance) {
            throw new Error("Error: Instantiation failed: Use SoundManager.getInstance() instead of new.");
        }
        SoundManager._instance = this;

        // Set up initialization on first user interaction
        const initSound = async () => {
            await this.initializeSoundContext();
            document.removeEventListener("click", initSound);
            document.removeEventListener("touchstart", initSound);
        };

        document.addEventListener("click", initSound);
        document.addEventListener("touchstart", initSound);
    }

    private async initializeSoundContext(): Promise<void> {
        if (this.soundContext) return;

        // Dynamically import PIXI sound only when needed
        try {
            const PixiSound = await import("@pixi/sound");
            this.soundContext = PixiSound.sound;
            this.soundContext.disableAutoPause = true;
        } catch (error) {
            console.error("Failed to initialize sound context:", error);
            // Optionally set a flag or notify the application that sound is unavailable
        }
    }

    //--------------------------------------------------------------------------
    //
    //  Methods
    //
    //--------------------------------------------------------------------------

    //----------------------------------
    //  Private:
    //----------------------------------

    private generateAlias(url: string): string {
        const slashIndex = url.lastIndexOf("/") + 1;
        const dotIndex = url.lastIndexOf(".");
        const alias = dotIndex > slashIndex ? url.substring(slashIndex, dotIndex) : url.substring(slashIndex);
        this.lastSoundAlias = alias;
        return alias;
    }

    private onFadeSoundComplete = (alias: string): void => {
        const soundObject: Sound = this.soundContext?.find(alias);
        if (soundObject.volume == 0) {
            this.stopSound(alias);
        }
    };

    //----------------------------------
    //  Public:
    //----------------------------------

    public static getInstance(): SoundManager {
        return SoundManager._instance;
    }

    public fadeSound(
        url: string,
        startVolume = 0,
        endVolume = 1,
        duration = 2,
        loop = true,
        completeCallback?: CompleteCallback,
    ): void {
        const alias = this.generateAlias(url);
        let soundObject: Sound = this.soundContext?.find(alias);
        if (soundObject) {
            if (soundObject.isPlaying) {
                startVolume = soundObject.volume;
            } else {
                this.soundContext?.play(alias, { loop: loop, complete: completeCallback });
            }
        } else {
            this.soundContext?.add(alias, url);
            soundObject = this.soundContext?.find(alias);
            this.soundContext?.play(alias, { loop: loop, complete: completeCallback });
        }
        if (startVolume == null) {
            startVolume = soundObject.volume;
        }
        gsap.fromTo(
            soundObject,
            { volume: startVolume },
            {
                volume: endVolume,
                duration: duration,
                onComplete: this.onFadeSoundComplete,
                onCompleteParams: [alias],
            },
        );
    }

    public loopSound(url: string): void {
        const alias = this.generateAlias(url);
        const soundObject: Sound = this.soundContext?.find(alias);
        if (soundObject) {
            if (soundObject.isPlaying) {
                return;
            } else {
                this.soundContext?.play(alias, { loop: true });
            }
        } else {
            this.soundContext?.add(alias, url);
            this.soundContext?.play(alias, { loop: true });
        }
    }

    private getSoundUrl(url: string): string {
        //if (typeof url === "string") {
            return url;
        //} else {
        //    return `${CDN}/generated/voice/1/audiomanager/${url.id}.mp3`; // adjust this line based on how you can get the url from AudioVO object
        //}
    }

    private isUrlAlias(url: string): boolean {
        return !url.includes("://");
    }

    private playExistingSound(alias: string, completeCallback?: CompleteCallback): Promise<Sound> {
        return new Promise((resolve) => {
            const soundObject: Sound = this.soundContext?.find(alias);
            this.soundContext?.play(alias, {
                complete: () => {
                    if (completeCallback) {
                        completeCallback(soundObject);
                    }
                    resolve(soundObject);
                },
            });
        });
    }

    private addAndPlaySound(alias: string, soundUrl: string, completeCallback?: CompleteCallback): Promise<Sound> {
        return new Promise((resolve) => {
            this.soundContext?.add(alias, soundUrl);
            this.soundContext?.play(alias, {
                complete: () => {
                    const soundObject: Sound = this.soundContext?.find(alias);
                    if (completeCallback) {
                        completeCallback(soundObject);
                    }
                    resolve(soundObject);
                },
            });
        });
    }

    //public async playBlankSound(): Promise<void> {
    //    await this.playSound(`${CDN}/audio/student/blank.mp3`);
    //}

    /**
     * @deprecated Please use the new version of the function with options
     */
    public async playSound(
        url: string,
        completeCallback: CompleteCallback,
        ignorePlaying?: boolean,
    ): Promise<Sound>;
    /**
     * Play a sound
     * @param url
     * @param options
     */
    public async playSound(url: string, options?: PlaySoundOptions): Promise<Sound>;
    public async playSound(
        url: string,
        optionsOrCallback?: CompleteCallback | PlaySoundOptions,
        ignorePlaying: boolean = false,
    ): Promise<Sound> {
        let options: PlaySoundOptions;
        if (typeof optionsOrCallback === "function") {
            // If the second argument is a function, treat it as the old version of the function
            options = {
                completeCallback: optionsOrCallback,
                ignorePlaying: ignorePlaying,
            };
        } else {
            // Otherwise, treat it as the new version of the function
            options = optionsOrCallback || {};
        }
        const soundUrl = this.getSoundUrl(url);
        const alias = this.isUrlAlias(soundUrl) ? soundUrl : this.generateAlias(soundUrl);
        this.lastSoundAlias = alias;
        const soundExists = this.soundContext?.exists(alias);

        if (options.stopSounds) this.stopAllSounds();

        if (soundExists) {
            const soundObject: Sound = this.soundContext?.find(alias);
            if (soundObject.isPlaying && !options.ignorePlaying) {
                return;
            } else {
                return this.playExistingSound(alias, options.completeCallback);
            }
        } else {
            return this.addAndPlaySound(alias, soundUrl, options.completeCallback);
        }
    }

    public async playSoundsInSequence(
        urls: (string)[],
        completeCallback?: CompleteCallback,
        stopSounds = false,
    ): Promise<void> {
        let soundObject: Sound | undefined;
        for (const url of urls) {
            soundObject = await this.playSound(url, { stopSounds: stopSounds });
        }
        if (completeCallback && soundObject) {
            completeCallback(soundObject);
        }
    }

    public removeAllSounds(): void {
        this.soundContext?.removeAll();
    }

    public removeSound(alias: string): void {
        if (this.soundContext?.find(alias)) {
            this.soundContext?.remove(alias);
        }
    }

    public stopAllSounds(): void {
        this.soundContext?.stopAll();
    }

    public stopLastSound(): void {
        this.stopSound(this.lastSoundAlias);
    }

    public stopSound(url: string): void {
        if (this.soundContext?.exists(url)) {
            this.soundContext?.stop(url);
            return;
        }

        const alias = url.substring(url.lastIndexOf("/") + 1, url.lastIndexOf("."));
        if (this.soundContext?.find(alias)) {
            this.soundContext?.stop(alias);
        }
    }

    public playByAlias(alias: string, onComplete: () => void) {
        this.soundContext?.play(alias, { complete: onComplete });
    }
}
